import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone } from "lucide-react";
import { NAV_LINKS, CONTACT } from "@/lib/site";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) window.lenis ? window.lenis.scrollTo(el, { offset: -80 }) : el.scrollIntoView({ behavior: "smooth" });
};

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        scrolled ? "bg-white/80 backdrop-blur-xl border-b border-black/5" : "bg-transparent"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-5 lg:px-8 h-[72px] flex items-center justify-between">
        <button data-testid="logo" onClick={() => scrollTo("hero")} className="flex items-center gap-2.5">
          <span className="grid place-items-center w-9 h-9 rounded-xl bg-navy text-white font-display font-black text-lg">A</span>
          <span className={`font-display font-extrabold text-lg tracking-tight ${scrolled ? "text-navy" : "text-navy"}`}>
            Apex<span className="text-brand">Credit</span>
          </span>
        </button>

        <ul className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <li key={l.id}>
              <button
                data-testid={`nav-${l.id}`}
                onClick={() => scrollTo(l.id)}
                className="px-3.5 py-2 text-sm font-medium text-ink/70 hover:text-brand transition-colors rounded-full"
              >
                {l.label}
              </button>
            </li>
          ))}
        </ul>

        <div className="hidden lg:flex items-center gap-3">
          <a data-testid="nav-call" href={`tel:${CONTACT.phoneRaw}`} className="flex items-center gap-2 text-sm font-semibold text-navy hover:text-brand transition-colors">
            <Phone className="w-4 h-4" /> {CONTACT.phone}
          </a>
          <button
            data-testid="nav-book-consultation"
            onClick={() => scrollTo("contact")}
            className="px-5 py-2.5 rounded-full bg-brand text-white text-sm font-semibold hover:bg-navy transition-colors"
          >
            Book Consultation
          </button>
        </div>

        <button data-testid="mobile-menu-toggle" className="lg:hidden text-navy" onClick={() => setOpen((o) => !o)}>
          {open ? <X /> : <Menu />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-white border-b border-black/5"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`mobile-nav-${l.id}`}
                  onClick={() => { scrollTo(l.id); setOpen(false); }}
                  className="text-left py-2.5 text-ink/80 font-medium"
                >
                  {l.label}
                </button>
              ))}
              <button
                onClick={() => { scrollTo("contact"); setOpen(false); }}
                className="mt-2 px-5 py-3 rounded-full bg-brand text-white font-semibold"
              >
                Book Consultation
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

export { scrollTo };
